import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronLeftIcon, Info, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { PAYROLL_TDS_SLAB } from "../../constants/api";
import { ContainerCard } from "../../components/ui/cards";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * TDS Configuration — the tax rate grid, per financial year.
 *
 * Rates are DATA, never code: they change every budget, and re-running an old
 * payroll has to reproduce the figures that were printed at the time. Nothing
 * here is seeded with real rates — the numbers must come from your CA.
 *
 * The new regime (115BAC) is the statutory default and needs no investment
 * declaration to compute, which is why it is the tab shown first. The old
 * regime additionally varies by age, so it gets the age selector.
 */

const REGIMES = [
    { key: "new", label: "New regime (115BAC)", hint: "Statutory default. No HRA / 80C / 80D — standard deduction only." },
    { key: "old", label: "Old regime", hint: "Opt-in. Allows HRA, Chapter VI-A and 24(b) from verified declarations." },
];

const AGE_CATEGORIES = [
    { key: "all", label: "All ages" },
    { key: "below_60", label: "Below 60" },
    { key: "senior", label: "Senior (60-79)" },
    { key: "super_senior", label: "Super senior (80+)" },
];

const emptyConfig = (regime) => ({
    regime,
    standard_deduction: "",
    rebate_87a_income_limit: "",
    rebate_87a_max_amount: "",
    cess_rate: "",
    surcharge_bands: [],
    surcharge_max_rate: "",
    marginal_relief_enabled: true,
    professional_tax_annual: "",
});

// Build the FY options around the current one: April starts a new year.
const financialYearOptions = () => {
    const now = new Date();
    const startYear = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return [startYear + 1, startYear, startYear - 1, startYear - 2].map((y) => `${y}-${y + 1}`);
};

const TDSConfiguration = () => {
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();
    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");

    const fyOptions = useMemo(financialYearOptions, []);
    const [financialYear, setFinancialYear] = useState(fyOptions[1] || fyOptions[0]);
    const [regime, setRegime] = useState("new");
    const [ageCategory, setAgeCategory] = useState("all");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tdsEnabled, setTdsEnabled] = useState(false);
    const [defaultRegime, setDefaultRegime] = useState("new");
    const [slabs, setSlabs] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [serverValidation, setServerValidation] = useState([]);

    const load = useCallback(async () => {
        const res = await fetch(PAYROLL_TDS_SLAB, { financial_year: financialYear }, setLoading);
        if (res?.status === "success") {
            setTdsEnabled(!!res.data.tds_enabled);
            setDefaultRegime(res.data.default_regime || "new");
            setSlabs(res.data.slabs || []);
            setConfigs(res.data.configs || []);
            setServerValidation(res.data.validation || []);
        } else {
            ToastMsg("error", res?.message || "Could not load TDS configuration");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financialYear]);

    useEffect(() => { load(); }, [load]);

    // The old regime is the only one with age-specific grids.
    useEffect(() => { if (regime === "new") setAgeCategory("all"); }, [regime]);

    // ---- current grid + config slice ----
    const visibleSlabs = useMemo(
        () => slabs
            .filter((s) => s.regime === regime && s.age_category === ageCategory)
            .sort((a, b) => Number(a.income_from) - Number(b.income_from)),
        [slabs, regime, ageCategory]
    );

    const currentConfig = useMemo(
        () => configs.find((c) => c.regime === regime) || emptyConfig(regime),
        [configs, regime]
    );

    // ---- client-side grid validation, mirroring tools/tds.validate_slabs ----
    // A gap does not throw at calculation time, it silently taxes the wrong
    // amount, so it is surfaced here before the save is even attempted.
    const gridProblems = useMemo(() => {
        const problems = [];
        if (!visibleSlabs.length) return ["No slabs configured for this regime yet"];
        if (Number(visibleSlabs[0].income_from) !== 0) problems.push("First slab must start at 0");
        visibleSlabs.forEach((slab, index) => {
            const isLast = index === visibleSlabs.length - 1;
            const rate = Number(slab.rate);
            if (Number.isNaN(rate) || rate < 0 || rate > 100) {
                problems.push(`Slab starting ${slab.income_from} has an invalid rate`);
            }
            if (isLast) {
                if (slab.income_to !== null && slab.income_to !== "" && slab.income_to !== undefined) {
                    problems.push("Highest slab must be left open-ended (blank upper limit)");
                }
                return;
            }
            if (slab.income_to === null || slab.income_to === "" || slab.income_to === undefined) {
                problems.push("Only the highest slab may be open-ended");
                return;
            }
            if (Number(slab.income_to) <= Number(slab.income_from)) {
                problems.push(`Slab ${slab.income_from}-${slab.income_to} has a non-increasing range`);
            }
            if (Number(visibleSlabs[index + 1].income_from) !== Number(slab.income_to)) {
                problems.push(`Gap or overlap between ${slab.income_to} and ${visibleSlabs[index + 1].income_from}`);
            }
        });
        return problems;
    }, [visibleSlabs]);

    // ---- slab row editing ----
    const patchSlab = (rowKey, patch) => setSlabs((prev) => prev.map((s) => (rowKeyOf(s) === rowKey ? { ...s, ...patch } : s)));

    const addSlab = () => {
        const last = visibleSlabs[visibleSlabs.length - 1];
        // A new top band starts where the previous one ended and is left open.
        setSlabs((prev) => [
            ...prev.map((s) => (last && rowKeyOf(s) === rowKeyOf(last) && (s.income_to === null || s.income_to === "")
                ? { ...s, income_to: "" } : s)),
            {
                _tmp: `tmp-${Date.now()}`,
                regime, age_category: ageCategory,
                income_from: last ? (last.income_to ?? "") : 0,
                income_to: null, rate: "",
            },
        ]);
    };

    const removeSlab = (rowKey) => setSlabs((prev) => prev.filter((s) => rowKeyOf(s) !== rowKey));

    const patchConfig = (patch) => setConfigs((prev) => {
        const existing = prev.find((c) => c.regime === regime);
        if (existing) return prev.map((c) => (c.regime === regime ? { ...c, ...patch } : c));
        return [...prev, { ...emptyConfig(regime), ...patch }];
    });

    // ---- surcharge bands ----
    const surchargeBands = currentConfig.surcharge_bands || [];
    const patchBand = (index, patch) => patchConfig({
        surcharge_bands: surchargeBands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    });
    const addBand = () => patchConfig({ surcharge_bands: [...surchargeBands, { from: "", rate: "" }] });
    const removeBand = (index) => patchConfig({ surcharge_bands: surchargeBands.filter((_, i) => i !== index) });

    const handleSave = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to change payroll configuration"); return; }
        if (gridProblems.length) { ToastMsg("error", "Fix the slab grid before saving"); return; }

        const payload = {
            financial_year: financialYear,
            tds_enabled: tdsEnabled,
            default_regime: defaultRegime,
            // Send only the grid on screen. The backend replaces that
            // (regime, age) grid wholesale, so other grids stay untouched.
            slabs: visibleSlabs.map((s) => ({
                regime, age_category: ageCategory,
                income_from: numOrZero(s.income_from),
                income_to: s.income_to === "" || s.income_to === null || s.income_to === undefined ? null : numOrZero(s.income_to),
                rate: numOrZero(s.rate),
            })),
            configs: [{
                regime,
                standard_deduction: numOrZero(currentConfig.standard_deduction),
                rebate_87a_income_limit: currentConfig.rebate_87a_income_limit === "" ? null : numOrZero(currentConfig.rebate_87a_income_limit),
                rebate_87a_max_amount: numOrZero(currentConfig.rebate_87a_max_amount),
                cess_rate: numOrZero(currentConfig.cess_rate),
                surcharge_bands: surchargeBands
                    .filter((b) => b.from !== "" && b.from !== null)
                    .map((b) => ({ from: numOrZero(b.from), rate: numOrZero(b.rate) })),
                surcharge_max_rate: currentConfig.surcharge_max_rate === "" ? null : numOrZero(currentConfig.surcharge_max_rate),
                marginal_relief_enabled: currentConfig.marginal_relief_enabled !== false,
                professional_tax_annual: numOrZero(currentConfig.professional_tax_annual),
            }],
        };

        const res = await update(PAYROLL_TDS_SLAB, payload, false, setSaving);
        if (res?.status === "success") {
            ToastMsg("success", "TDS configuration saved");
            load();
        } else {
            const problems = res?.data?.problems;
            ToastMsg("error", problems?.length ? problems[0] : (res?.message || "Could not save configuration"));
        }
    };

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                <p className="text-md text-gray-700 dark:text-gray-300">Loading TDS configuration...</p>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => navigate("/payroll/payrollprocess")}
                />
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TDS Configuration</h1>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-1">
                        Income tax slabs and constants per financial year — used by payroll to compute TDS
                    </p>
                </div>
            </div>

            {/* Rates-are-data notice */}
            <div className="mb-6 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-4 flex gap-3">
                <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 dark:text-amber-200">
                    <p className="font-semibold mb-1">Enter the rates your CA confirms — nothing is pre-filled.</p>
                    <p>
                        Slabs are stored against the financial year, so changing next year's rates never alters a payslip
                        already issued. Until slabs exist for a year, payroll deducts <strong>zero</strong> TDS rather than
                        guessing a rate.
                    </p>
                </div>
            </div>

            {/* Company-level switches */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Financial year</label>
                    <select
                        value={financialYear}
                        onChange={(e) => setFinancialYear(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25 focus:border-sidebar-primary"
                    >
                        {fyOptions.map((fy) => <option key={fy} value={fy}>{fy}</option>)}
                    </select>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Deduct TDS in payroll</label>
                    <button
                        type="button"
                        onClick={() => setTdsEnabled((v) => !v)}
                        className={cn("inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                            tdsEnabled
                                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300")}
                    >
                        <span className={cn("h-2.5 w-2.5 rounded-full", tdsEnabled ? "bg-emerald-500" : "bg-gray-400")} />
                        {tdsEnabled ? "Enabled" : "Disabled"}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">Off = payroll runs exactly as before, deducting no tax.</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Default regime</label>
                    <select
                        value={defaultRegime}
                        onChange={(e) => setDefaultRegime(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25 focus:border-sidebar-primary"
                    >
                        {REGIMES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                    <p className="text-xs text-gray-400 mt-2">Applied to anyone who has not opted in.</p>
                </div>
            </div>

            {/* Regime tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {REGIMES.map((r) => (
                    <button
                        key={r.key}
                        type="button"
                        onClick={() => setRegime(r.key)}
                        className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                            regime === r.key
                                ? "bg-sidebar-primary text-white"
                                : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")}
                    >
                        {r.label}
                    </button>
                ))}
                {regime === "old" && (
                    <select
                        value={ageCategory}
                        onChange={(e) => setAgeCategory(e.target.value)}
                        className="ml-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25"
                    >
                        {AGE_CATEGORIES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                    </select>
                )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {REGIMES.find((r) => r.key === regime)?.hint}
                {regime === "old" && " Age changes the grid only under this regime."}
            </p>

            {/* Grid problems */}
            {gridProblems.length > 0 && (
                <div className="mb-4 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                        <span className="text-sm font-semibold text-rose-800 dark:text-rose-300">
                            This grid cannot be saved yet
                        </span>
                    </div>
                    <ul className="list-disc list-inside text-sm text-rose-700 dark:text-rose-300 space-y-0.5">
                        {gridProblems.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                </div>
            )}
            {serverValidation.length > 0 && (
                <div className="mb-4 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Saved grids with problems</p>
                    <ul className="list-disc list-inside text-sm text-amber-700 dark:text-amber-300 space-y-0.5">
                        {serverValidation.map((v, i) => <li key={i}>{v.regime}/{v.age_category}: {v.problem}</li>)}
                    </ul>
                </div>
            )}

            {/* Slab grid */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Marginal slab bands</h2>
                    <button
                        type="button" onClick={addSlab}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-sidebar-primary text-white hover:brightness-90 transition"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add band
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <th className="px-4 py-3 font-medium">Income from (₹)</th>
                                <th className="px-4 py-3 font-medium">Income to (₹)</th>
                                <th className="px-4 py-3 font-medium">Rate (%)</th>
                                <th className="px-4 py-3 font-medium w-16"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {visibleSlabs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                                        No bands yet — add the lowest band starting at 0.
                                    </td>
                                </tr>
                            ) : visibleSlabs.map((slab, index) => {
                                const key = rowKeyOf(slab);
                                const isLast = index === visibleSlabs.length - 1;
                                return (
                                    <tr key={key} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/30">
                                        <td className="px-4 py-3">
                                            <NumInput value={slab.income_from} onChange={(v) => patchSlab(key, { income_from: v })} />
                                        </td>
                                        <td className="px-4 py-3">
                                            {isLast ? (
                                                <span className="text-xs text-gray-500 dark:text-gray-400 italic">and above (open-ended)</span>
                                            ) : (
                                                <NumInput value={slab.income_to ?? ""} onChange={(v) => patchSlab(key, { income_to: v })} />
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <NumInput value={slab.rate} step="0.5" onChange={(v) => patchSlab(key, { rate: v })} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button type="button" onClick={() => removeSlab(key)} className="text-gray-400 hover:text-rose-500 transition-colors">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                    Each rate applies only to the portion of income inside its band. Bands must run contiguously from 0
                    with the highest one left open-ended.
                </p>
            </div>

            {/* Per-regime constants */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    Constants — {REGIMES.find((r) => r.key === regime)?.label}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Standard deduction (₹)" value={currentConfig.standard_deduction} onChange={(v) => patchConfig({ standard_deduction: v })} />
                    <Field label="Section 87A — income limit (₹)" value={currentConfig.rebate_87a_income_limit} onChange={(v) => patchConfig({ rebate_87a_income_limit: v })} />
                    <Field label="Section 87A — max rebate (₹)" value={currentConfig.rebate_87a_max_amount} onChange={(v) => patchConfig({ rebate_87a_max_amount: v })} />
                    <Field label="Health & education cess (%)" value={currentConfig.cess_rate} step="0.1" onChange={(v) => patchConfig({ cess_rate: v })} />
                    <Field label="Surcharge cap (%)" value={currentConfig.surcharge_max_rate} step="0.1" onChange={(v) => patchConfig({ surcharge_max_rate: v })} />
                    {regime === "old" && (
                        <Field label="Professional tax, annual (₹)" value={currentConfig.professional_tax_annual} onChange={(v) => patchConfig({ professional_tax_annual: v })} />
                    )}
                </div>

                <label className="mt-4 inline-flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={currentConfig.marginal_relief_enabled !== false}
                        onChange={(e) => patchConfig({ marginal_relief_enabled: e.target.checked })}
                        className="rounded border-gray-300 text-sidebar-primary focus:ring-sidebar-primary/30 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                        Apply marginal relief
                        <span className="text-xs text-gray-400 ml-1">
                            (crossing a surcharge threshold can never cost more tax than the income gained)
                        </span>
                    </span>
                </label>

                {/* Surcharge bands */}
                <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Surcharge bands</h3>
                        <button
                            type="button" onClick={addBand}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                        >
                            <Plus className="h-3.5 w-3.5" /> Add band
                        </button>
                    </div>
                    {surchargeBands.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            None configured. Add a band for each threshold that carries surcharge — anyone above ₹50L
                            needs this, and it is the fiddliest part of the calculation.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {surchargeBands.map((band, index) => (
                                <div key={index} className="flex flex-wrap items-center gap-3">
                                    <span className="text-xs text-gray-500 dark:text-gray-400 w-24">Above (₹)</span>
                                    <NumInput value={band.from} onChange={(v) => patchBand(index, { from: v })} />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">rate (%)</span>
                                    <NumInput value={band.rate} step="0.5" onChange={(v) => patchBand(index, { rate: v })} />
                                    <button type="button" onClick={() => removeBand(index)} className="text-gray-400 hover:text-rose-500 transition-colors">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    Saving replaces only the grid shown above ({regime}{regime === "old" ? ` / ${ageCategory}` : ""}).
                </p>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !editPermission || gridProblems.length > 0}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <Save className="h-4 w-4" />}
                    Save configuration
                </button>
            </div>
        </ContainerCard>
    );
};

const rowKeyOf = (slab) => slab.id ?? slab._tmp;
const numOrZero = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));

const NumInput = ({ value, onChange, step = "1" }) => (
    <input
        type="number" step={step} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-32 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-right text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25 focus:border-sidebar-primary"
    />
);

const Field = ({ label, value, onChange, step = "1" }) => (
    <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
        <input
            type="number" step={step} value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25 focus:border-sidebar-primary"
        />
    </div>
);

export default TDSConfiguration;
